/**
 * WP8 · BK-Detail — linke Sidebar (sticky, über alle Tabs sichtbar).
 *
 * Schlanke BK-Variante des PB-Musters (`pflege/profile/PbSidebar.tsx`):
 * Identity-Card (Avatar, Name, Rating, Status/Qualifikation), Zuweisung
 * (assigned admin, inline speicherbar), Kern-Metadaten, Personenangaben,
 * Standort-Karte und Freitext-Beschreibung.
 *
 * Bewusst NICHT übernommen: Phasen-Stepper (PB-only), Setcard-Aktionen
 * (downloadPbSetcard/SetcardSharePanel sind PB-spezifische Endpunkte).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Card } from '../../../components/ui';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import { type V1User, type V1Profile, type V1Admin, primaryProfile, selectableAdmins, v1Admins, v1Users } from '../../../lib/v1';
import { HARDCODED, BK_HARDCODED, bkStatusLabel } from './options';
import { Rating } from '../../pflege/profile/controls';
import { formatDate } from '../../../lib/dateFormat';
import { PbMapWidget } from '../../pflege/PbMapWidget';
import { PhoneLink } from '../../../components/PhoneLink';
import { useOptionLabel } from '../../../lib/useOptionLabel';

interface BkSidebarProps {
  user: V1User | null;
  dark: boolean;
}

export function BkSidebar({ user, dark }: BkSidebarProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const optLabel = useOptionLabel();
  const profile: V1Profile | null = user ? primaryProfile(user) : null;

  // ── Zugewiesen an (assigned admin) — inline editierbar (Muster PbSidebar) ──
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  const [assignedTo, setAssignedTo] = useState<number | null>(
    user?.assigned_to != null ? Number(user.assigned_to) : null,
  );
  const [savingAssign, setSavingAssign] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => { if (alive) setAdmins(list); })
      .catch(() => {/* Feld degradiert zur Id-Anzeige */});
    return () => { alive = false; };
  }, []);

  const assignedAdmin = useMemo(
    () => admins.find(a => a.id === assignedTo) ?? null,
    [admins, assignedTo],
  );
  const adminName = (a: V1Admin): string =>
    [a.vorname, a.nachname].filter(Boolean).join(' ') || a.email || `Admin #${a.id}`;

  const handleAssign = async (next: number | null) => {
    if (!user?.id || savingAssign) return;
    const prev = assignedTo;
    setAssignedTo(next);          // optimistisch
    setSavingAssign(true);
    setAssignError(null);
    try {
      await v1Users.update(user.id, { assigned_to: next });
    } catch (e) {
      setAssignedTo(prev);        // Rollback
      setAssignError(e instanceof Error ? e.message : tr('betreuung.sidebar.assign_error'));
    } finally {
      setSavingAssign(false);
    }
  };

  const initials = useMemo(() => {
    const v = String(profile?.vorname ?? '')[0] ?? '';
    const n = String(profile?.nachname ?? '')[0] ?? '';
    // Als „Name" an <Avatar> — zwei Buchstaben, getrennt, damit Avatar sie übernimmt.
    return [v, n].filter(Boolean).join(' ').toUpperCase() || 'B K';
  }, [profile?.vorname, profile?.nachname]);

  const name = [profile?.titel, profile?.vorname, profile?.nachname]
    .filter(Boolean).join(' ') || tr('pflege.detail.new_profile');

  const age = useMemo(() => {
    if (!profile?.geburtsdatum) return null;
    const d = new Date(profile.geburtsdatum);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 31557600000);
  }, [profile?.geburtsdatum]);

  const statusLabel = bkStatusLabel(user?.status, tr) ?? '—';
  // Ton nach Status-Code (0 Verfügbar / 1 In Betreuung = grün, 2 Pausierend = blau, Rest = amber)
  const statusCode = user?.status == null ? null : Number(user.status);
  const statusTone = statusCode === 0 || statusCode === 1
    ? { bg: dark ? '#0f3a23' : '#ecfdf3', color: dark ? '#86efac' : '#027a48' }
    : statusCode === 2
      ? { bg: dark ? '#1a2a3a' : '#eff8ff', color: dark ? '#7dd3fc' : '#026aa2' }
      : { bg: dark ? '#3a2a0f' : '#fffaeb', color: dark ? '#fcd34d' : '#b54708' };

  const qualifikationLabel = (() => {
    const v = strOr(profile?.qualifikation);
    if (!v) return null;
    return optLabel(BK_HARDCODED.qualifikation.find(o => o.value === v)?.label ?? v);
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Identity card */}
      <Card dark={dark} style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {/* Feedback 11.10.2026: Profilbild (Foto „Person"), sonst Initialen. */}
          <Avatar name={initials} userId={user?.id ?? null} size={56} dark={dark} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.heading, lineHeight: 1.2 }}>{name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <span style={{ fontSize: 12, color: t.muted }}>{tr('betreuung.sidebar.role')}</span>
              <Rating dark={dark} value={Number(user?.rating ?? 0)} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            background: statusTone.bg, color: statusTone.color,
            borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 600,
          }}>{statusLabel}</span>
          {qualifikationLabel && (
            <span style={{
              background: dark ? '#2a1222' : BRAND_LIGHT,
              color: dark ? '#f49dd1' : BRAND_DARK,
              borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 600,
            }}>{qualifikationLabel}</span>
          )}
        </div>

        {/* Zugewiesen an — Admin mit Avatar + Inline-Reassign */}
        {user?.id != null && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.borderSoft}` }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: t.muted,
              textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
            }}>{tr('pflege.form.field.assigned_to')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {assignedAdmin
                ? <Avatar name={adminName(assignedAdmin)} adminId={assignedAdmin.id} size={34} tone="blue" dark={dark} />
                : <div style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: t.chipBg, color: t.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
                  }}>—</div>}
              <select
                aria-label={tr('pflege.form.field.assigned_to')}
                value={assignedTo ?? ''}
                disabled={savingAssign}
                onChange={e => handleAssign(e.target.value ? Number(e.target.value) : null)}
                style={{
                  flex: 1, minWidth: 0, fontSize: 13, fontFamily: 'Outfit,sans-serif',
                  border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 9px',
                  background: t.inputBg, color: t.text, cursor: savingAssign ? 'wait' : 'pointer',
                }}
              >
                <option value="">{tr('pflege.form.ph.assigned_to')}</option>
                {selectableAdmins(admins, [assignedTo]).map(a => (
                  <option key={a.id} value={a.id}>{adminName(a)}</option>
                ))}
              </select>
            </div>
            {assignError && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: '#b42318' }}>{assignError}</div>
            )}
          </div>
        )}
      </Card>

      {/* Kern-Metadaten */}
      <Card dark={dark} style={{ padding: 0 }}>
        <Row dark={dark} label={tr('betreuung.sidebar.verfuegbarkeit')}>{fmtDate(user?.startdatum)}</Row>
        {strOr(user?.wage) ? <Row dark={dark} label={tr('pflege.sidebar.net_wage')}>{`${strOr(user!.wage)} €`}</Row> : null}
        {strOr(user?.pause_von) ? (
          <Row dark={dark} label={tr('pflege.form.field.pause_von')} tone="warn">{fmtDate(user!.pause_von)}</Row>
        ) : null}
        {strOr(user?.pause_bis) ? (
          <Row dark={dark} label={tr('pflege.form.field.pause_bis')} tone="warn">{fmtDate(user!.pause_bis)}</Row>
        ) : null}
      </Card>

      {/* Personenangaben */}
      {profile && (
        <Card dark={dark} style={{ padding: 0 }}>
          <SectionHead dark={dark}>{tr('pflege.sidebar.section_person')}</SectionHead>
          {profile.geburtsdatum && (
            <Row dark={dark} label={tr('pflege.form.field.geburtsdatum')}>{fmtDate(profile.geburtsdatum)}{age != null ? ` · ${age} ${tr('pflege.sidebar.years_abbr')}` : ''}</Row>
          )}
          {profile.groesse != null && <Row dark={dark} label={tr('pflege.form.field.groesse')}>{String(profile.groesse)} cm</Row>}
          {profile.gewicht != null && <Row dark={dark} label={tr('pflege.form.field.gewicht')}>{String(profile.gewicht)} kg</Row>}
          {strOr(user?.telefon) && <Row dark={dark} label={tr('pflege.form.field.telefon')}><PhoneLink number={strOr(user!.telefon)} dark={dark} /></Row>}
          {user?.mobile && <Row dark={dark} label={tr('pflege.form.field.mobile')}><PhoneLink number={user.mobile} dark={dark} /></Row>}
          {user?.email && <Row dark={dark} label={tr('pflege.form.field.email')}><ContactLink kind="mailto" value={user.email} /></Row>}
        </Card>
      )}

      {/* Standort-Karte (user-generisches Widget, liest user/profile-Adresse) */}
      {(strOr(user?.strasse) || strOr(user?.ort) || strOr(profile?.strasse) || strOr(profile?.ort)) && (
        <PbMapWidget user={user} dark={dark} mode="compact" />
      )}

      {/* Freitext-Beschreibung */}
      {strOr(user?.description) ? (
        <Card dark={dark} style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            {tr('pflege.sidebar.freetext')}
          </div>
          <div style={{ fontSize: 13, color: t.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {strOr(user!.description)}
          </div>
        </Card>
      ) : null}

    </div>
  );
}

/* ─── helpers (Muster PbSidebar) ───────────────────────────────────────── */

/** Klickbarer tel:/mailto:-Link in Markenfarbe (Anzeige-Stellen, keine Inputs). */
function ContactLink({ kind, value }: { kind: 'tel' | 'mailto'; value: string }) {
  const href = kind === 'tel' ? `tel:${value.replace(/[^\d+]/g, '')}` : `mailto:${value}`;
  return (
    <a href={href} style={{ color: BRAND, textDecoration: 'none', wordBreak: 'break-all' }}>
      {value}
    </a>
  );
}

function Row({
  label, children, dark, tone,
}: {
  label: string;
  children: React.ReactNode;
  dark: boolean;
  tone?: 'warn';
}) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 12, padding: '10px 16px',
      borderBottom: `1px solid ${t.borderSoft}`,
      fontSize: 13,
    }}>
      <span style={{ color: t.muted, fontWeight: 500 }}>{label}</span>
      <span style={{
        color: tone === 'warn' ? '#b42318' : t.text,
        fontWeight: 500, textAlign: 'right',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: '60%',
      }}>{children}</span>
    </div>
  );
}

function SectionHead({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      padding: '12px 16px 6px',
      fontSize: 11, fontWeight: 700, color: t.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      borderBottom: `1px solid ${t.borderSoft}`,
    }}>{children}</div>
  );
}

/** String-Form von `v`, wenn nicht-leeres Primitiv, sonst '' (falsy). */
function strOr(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'object') return '';
  return String(v);
}

function fmtDate(v: unknown): string {
  if (v == null || v === '') return '—';
  const s = String(v);
  if (/^\d{2}\.\d{2}\.\d{4}/.test(s)) return s.slice(0, 10);
  // Deutsches Format unabhängig von der UI-Sprache; Unparsbares unverändert.
  return formatDate(s, s);
}

function lookupHardcoded(key: 'status' | 'konto' | 'badge' | 'impfstatus', value: string): string | null {
  const list = HARDCODED[key];
  if (!list) return null;
  const match = (list as Array<string | { value: string; label: string }>).find(o => {
    if (typeof o === 'string') return o === value;
    return o.value === value;
  });
  return match ? (typeof match === 'string' ? match : match.label) : null;
}
